import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  NodeResizeControl,
  NodeToolbar,
  Position,
  ResizeControlVariant,
  useStoreApi,
  type Node,
  type NodeProps,
} from '@xyflow/react'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import type { UpdateCanvasNote } from '@/src/renderer/lib/db/models'
import { BUMP_INSET, buildUnionPath, UNION_CORNER_RADIUS } from '@/src/renderer/components/layout/ContentUnionShape/unionPath'
import { CanvasAreaIcon } from '../icons'
import { CanvasOptionsBar } from '../optionsBar'
import { OPTIONS_BAR_OFFSET } from '../optionsBar/CanvasOptionsBar.types'
import { useCanvasPointerBusy, useOptionsBarPlacement, useSoloSelected } from '../hooks/useCanvasOptionsBar'
import { usePresence } from '../hooks/usePresence'
import { useOptionPreview } from '../hooks/useOptionPreview'
import { getCanvasNoteRingColor } from '../utils/canvasNoteStyle'
import { releaseTextSelection } from '../utils/canvasTextSelection'
import { DEFAULT_CANVAS_NOTE_COLOR } from '../utils/canvasNoteStyle'
import { AREA_TAB_HEIGHT, MIN_AREA_SIZE, getAreaColor } from '../utils/canvasArea'
import type { ResizeCorner } from '../utils/canvasAlignment'
import type { AreaNodeData } from './AreaNode.types'
import './AreaNode.scss'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'

const CORNERS: readonly ResizeCorner[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right']
const EDGES = ['top', 'right', 'bottom', 'left'] as const
/** Innenabstand des Reiters LINKS. Rechts gibt es keinen: Dort ist die S-Kurve der Abstand. */
const TAB_PADDING = 14
/** Die Kurve darf so weit unter den Text greifen — sie läuft flach an, und ein Stück davon ist noch volle Höhe. */
const TAB_CURVE_OVERLAP = 12
/** Icon und Lücke davor, aus dem Design. */
const TAB_ICON = 24
const TAB_GAP = 6
/** Grobe Schätzung, solange nichts gemessen ist: ein Zeichen bei 16px Medium. */
const CHAR_WIDTH = 9

/**
 * Der **Bereich** — eine Karte mit Reiter, die Zettel räumlich zusammenfasst
 * (CONTEXT.md, `.claude/tasks/canvas-area.md`, ADR 0012).
 *
 * Der Knoten IST die Karte: Lage und Maße aus der Datenbank, der Reiter wächst
 * darüber hinaus nach oben, wenn es einen Namen gibt. Angefasst wird er überall
 * — Klick wählt, Zug verschiebt samt Inhalt; das erledigt `CanvasFlow`, der
 * die Mitglieder beim Anfassen einfriert.
 *
 * Die Form kommt aus `buildUnionPath` — derselbe Pfad wie im Fensterrahmen
 * (`ContentUnionShape`), hier mit dem Reiter ganz links: einfache Ecke links,
 * S-Kurve rechts.
 *
 * **Der Name** folgt dem Modell der Notiz: `title === null` heißt „kein
 * Reiter", `""` heißt „an, aber leer". Getippt wird im Reiter selbst, im
 * **Bearbeiten-Modus** mit denselben Ausgängen wie beim Text eines Zettels
 * (Escape, Eingabe, ⌘/⌃+Eingabe — und der Bereich bleibt danach ausgewählt).
 */
export const AreaNode: React.FC<NodeProps<Node<AreaNodeData>>> = ({ data, selected, width: nodeWidth, height: nodeHeight }) => {
  const { note, editingEnabled = true, onResizeStart, onEdit, onDelete, onFitToContent, onEditingChange, onFrameGrab, colorPreview } = data
  const { t } = useTranslation()
  const dataSource = useDataSource()
  const store = useStoreApi()
  const width = nodeWidth ?? note.width
  const height = nodeHeight ?? note.height
  // Die Vorschau des Farbwählers färbt den Bereich — Leiste und Verlaufsschritt lesen `note.color`.
  const [ownPreview, setOwnPreview] = useOptionPreview(note.color)
  const shownColor = (ownPreview ?? colorPreview)?.color ?? note.color
  const color = getAreaColor(shownColor)

  const edit = useCallback((before: UpdateCanvasNote, after: UpdateCanvasNote) => {
    if (onEdit) onEdit(note.id, before, after)
    else void dataSource.updateCanvasNote(note.id, after)
  }, [onEdit, note.id, dataSource])

  /*
    Der ENTWURF des Namens ist die Wahrheit fürs Zeichnen, nicht `note.title`
    (wie `titleDraft` der Notiz): Nach dem Festschreiben zieht die Datenbank
    erst mit der Live-Query nach, und für diesen Frame stünde sonst der alte
    Name — oder der Reiter verschwände kurz. Umgekehrt spiegelt er den
    Datensatz, sobald nicht getippt wird; nie mittendrin, sonst setzte die
    Live-Query den Namen zurück, während der Schreibvorgang noch läuft.
  */
  const [draft, setDraft] = useState<string | null>(note.title)
  const [isEditing, setIsEditing] = useState(false)
  const isEditingRef = useRef(isEditing)
  isEditingRef.current = isEditing
  useEffect(() => {
    if (!isEditingRef.current) setDraft(note.title)
  }, [note.title])
  const named = draft !== null
  /*
    Der zuletzt FESTGESCHRIEBENE Name — damit ein versehentliches Ausschalten
    ihn nicht verschluckt: Wer gleich wieder einschaltet, bekommt zurück, was
    dastand (gemeldet 2026-09-05). Der Entwurf zählt nicht mit; was mitten im
    Tippen verworfen wurde, ist verworfen (siehe `handleToggleTitle`). Ein
    Ref und kein State: Nichts im Bild hängt daran, und ein State erzwänge
    ein zweites Zeichnen je Tastendruck.
  */
  const lastNameRef = useRef('')
  if (note.title) lastNameRef.current = note.title
  const inputRef = useRef<HTMLInputElement>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const focusNode = useCallback(() => {
    rootRef.current?.closest<HTMLElement>('.react-flow__node')?.focus({ preventScroll: true })
  }, [])

  /*
    **Beim Öffnen steht der ganze Name in der Auswahl** — wie beim Umbenennen
    einer Datei. Ein Bereich kommt benannt auf die Fläche („Bereich"), und wer
    ihn öffnet, will diesen Namen ERSETZEN; müsste man erst alles markieren,
    tippte man beim ersten Mal hinter den Vorgabenamen.

    Im Effekt und nicht im `autoFocus`: Das Feld gibt es erst nach dem Aufbau.
  */
  useEffect(() => {
    if (isEditing) inputRef.current?.select()
  }, [isEditing])

  // Beginn und Ende melden; beim Verschwinden des Nodes gilt „nicht mehr".
  const onEditingChangeRef = useRef(onEditingChange)
  onEditingChangeRef.current = onEditingChange
  useEffect(() => {
    onEditingChangeRef.current?.(note.id, isEditing)
    return () => { if (isEditing) onEditingChangeRef.current?.(note.id, false) }
  }, [isEditing, note.id])

  /*
    Hinein: Der Bereich wird dabei AUSGEWÄHLT (die Auswahl schrumpft auf ihn,
    `addSelectedNodes` ersetzt sie) — wie beim Doppelklick auf einen Zettel.
    Die harte Regel „abgewählt heißt nicht im Bearbeiten-Modus" findet im
    Store so sofort einen ausgewählten Bereich vor.
  */
  const openEditor = useCallback(() => {
    if (!editingEnabled) return
    store.getState().addSelectedNodes([note.id])
    setIsEditing(true)
  }, [editingEnabled, store, note.id])

  /*
    Der EINE Ausstieg. Schreibt den Namen fest, wenn er sich geändert hat —
    ein Schritt für **Rückgängig** —, und lässt den Bereich ausgewählt
    zurück. Der Riegel hält den `blur` ab, den das Abräumen selbst auslöst.
    Der zweite Wächter (`isEditingRef`) fängt den `blur` eines Feldes ab,
    das gerade auf anderem Weg verschwindet (Titel aus, siehe unten): Der
    getippte Name darf dann nicht hinter dem Ausschalten her geschrieben werden.
  */
  const committingRef = useRef(false)
  const commit = useCallback(() => {
    if (committingRef.current || !isEditingRef.current) return
    committingRef.current = true
    try {
      isEditingRef.current = false
      setIsEditing(false)
      store.getState().addSelectedNodes([note.id])
      if (draft !== note.title) edit({ title: note.title }, { title: draft })
      // Festgeschrieben heißt: erinnert. Die Datenbank zieht erst mit der
      // Live-Query nach, und bis dahin wäre der vorige Name der erinnerte.
      if (draft) lastNameRef.current = draft
      releaseTextSelection(inputRef.current)
    } finally {
      committingRef.current = false
    }
  }, [draft, note.title, note.id, edit, store])
  const commitRef = useRef(commit)
  commitRef.current = commit

  /*
    **Abgewählt heißt: nicht im Bearbeiten-Modus** (CONTEXT.md). Der Klick auf
    die Fläche löst kein `blur` aus — harte Regel bei jedem Render ohne
    Auswahl. Die Wahrheit über die Auswahl steht im Store: Die Prop kann
    einen Render hinterherhinken (Doppelklick wählt aus UND öffnet im selben
    Tick). Und im Hand-Werkzeug endet ein laufender Bearbeiten-Modus ebenso.
  */
  useEffect(() => {
    if (selected || store.getState().nodeLookup.get(note.id)?.selected) return
    if (isEditingRef.current) commitRef.current()
  }, [selected, isEditing, store, note.id])
  useEffect(() => {
    if (!editingEnabled && isEditingRef.current) commitRef.current()
  }, [editingEnabled])

  /*
    Titel an/aus — dasselbe Modell wie bei der Notiz, mit einem Unterschied:
    **Anschalten öffnet den Namen sofort zum Tippen.** Die Notiz hat neben dem
    Titel noch ihren Text, der Bereich hat nur den Namen — wer „Titel" drückt,
    will ihn schreiben, nicht erst noch einmal doppelklicken.
  */
  const nameIt = useCallback(() => {
    if (!named) {
      // Zurück, was zuletzt dastand — leer nur, wenn es nie einen Namen gab.
      const restored = lastNameRef.current
      setDraft(restored)
      /*
        Das „Vorher" ist `null`, und zwar unabhängig von der Prop: `!named`
        HEISST, dass kein Titel gespeichert ist. Die Live-Query zieht erst
        nach — wer schnell aus- und wieder einschaltet, bekäme über
        `note.title` sonst den alten Namen als Vorher-Wert, und ⌘Z stellte
        einen Zustand her, den es nie gab.
      */
      edit({ title: null }, { title: restored })
    }
    openEditor()
  }, [named, edit, openEditor])
  const handleToggleTitle = useCallback(() => {
    if (!named) { nameIt(); return }
    // Aus, auch mitten im Tippen: Was im Feld stand, verfällt (siehe `commit`).
    isEditingRef.current = false
    setIsEditing(false)
    setDraft(null)
    // Das „Vorher" ist der FESTGESCHRIEBENE Name: beim Tippen die Prop (der
    // Entwurf gilt ja nicht), sonst der Entwurf — er spiegelt den Datensatz
    // und kennt unsere eigenen Schreibvorgänge, bevor die Live-Query nachzieht.
    edit({ title: isEditing ? note.title : draft }, { title: null })
  }, [named, nameIt, edit, isEditing, note.title, draft])

  /*
    Die Eingabetaste öffnet den Namen des ausgewählten Bereichs — und benennt
    ihn, falls er noch keinen hat (dasselbe wie der Umschalter). Am `window`
    wie bei der Notiz: Beim Auswählen bekommt React Flows Node-Hülle den
    Fokus, das ELTERNelement der Karte. Nur allein ausgewählt: In einer
    Mehrfachauswahl gilt Enter niemandem.
  */
  const soloSelected = useSoloSelected(selected)
  useEffect(() => {
    if (!selected || !soloSelected || isEditing || !editingEnabled) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      e.preventDefault()
      nameIt()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selected, soloSelected, isEditing, editingEnabled, nameIt])

  /*
    Hinaus über `blur`, nicht direkt über `commit`: Der Fokus soll danach auf
    dem NODE liegen, damit Entf am Canvas ankommt — und alles läuft einmal
    (`blur` → `commit` → Fokus auf den Node). Blankes Enter zählt hier mit:
    Ein Name hat keine zweite Zeile.
  */
  const handleInputKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Escape' && event.key !== 'Enter') return
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.blur()
    focusNode()
  }, [focusNode])

  /*
    Der Reiter ist so breit wie sein Inhalt, höchstens so breit wie die Karte
    (Grillen 2026-09-04: ein langer Name endet mit Ellipse, nie steht der
    Reiter über). Leer zählt der Platzhalter, damit er beim ersten Tippen
    nicht springt.

    **Gemessen, nicht geschätzt** (Anpassung 2026-09-05: der Reiter war zu
    breit, die Kante stand weit rechts vom Text): Ein verborgener Zwilling des
    Textes in derselben Schrift liefert die Breite — auch beim Tippen, dann
    bekommt das Feld genau sie. Die Schätzung (9px je Zeichen) gilt nur, wo
    nichts gemessen werden kann (verborgener Tab, jsdom), und bewusst ohne
    Schleife: Misst der Zwilling 0, bleibt der Schätzwert stehen.

    Zwei Breiten: Der DOM-Reiter endet mit dem Text — rechts gibt es keinen
    Innenabstand, die S-Kurve selbst ist der Abstand (sie läuft flach an, und
    ein Innenabstand davor riss eine sichtbare Lücke). Für den PFAD kommt
    `BUMP_INSET` dazu, denn dort setzt die Kurve genau `BUMP_INSET` vor dem
    Pfad-Rand an: Sie beginnt also direkt hinter dem Text.
  */
  const placeholder = t('projectCanvas.area.namePlaceholder')
  const label = draft || placeholder
  const measureRef = useRef<HTMLSpanElement>(null)
  const [measured, setMeasured] = useState(0)
  useLayoutEffect(() => {
    // Bruchteile zählen — `offsetWidth` rundet, und ein abgerundeter Reiter
    // schnitt den Platzhalter mit Ellipse ab, obwohl Platz war. Der Kasten ist
    // mit dem Ausschnitt skaliert, deshalb durch den Zoom; aufgerundet, damit
    // der Text immer hineinpasst.
    const rect = measureRef.current?.getBoundingClientRect()
    const zoom = store.getState().transform[2] || 1
    const w = rect ? Math.ceil(rect.width / zoom) : 0
    if (w > 0) setMeasured(prev => (prev === w ? prev : w))
  }, [label, named, store])
  const textWidth = measured > 0 ? measured : label.length * CHAR_WIDTH
  const tabDomWidth = useMemo(() => {
    if (!named) return 0
    return Math.min(Math.max(width - BUMP_INSET, 0), TAB_PADDING + TAB_ICON + TAB_GAP + textWidth)
  }, [named, width, textWidth])
  const tabWidth = named ? tabDomWidth + BUMP_INSET - TAB_CURVE_OVERLAP : 0

  const path = useMemo(() => buildUnionPath(
    { x: 0, y: named ? AREA_TAB_HEIGHT : 0, w: width, h: height },
    named ? { x: 0, y: 0, w: tabWidth, h: AREA_TAB_HEIGHT } : null,
    true,
  ), [named, width, height, tabWidth])

  const pointerBusy = useCanvasPointerBusy(selected)
  const placement = useOptionsBarPlacement(note.y - (named ? AREA_TAB_HEIGHT : 0), selected)
  const showBar = selected && soloSelected && !pointerBusy
  const barPresence = usePresence(showBar)
  const returnFocus = useCallback(() => {
    if (isEditingRef.current) inputRef.current?.focus({ preventScroll: true })
    else focusNode()
  }, [focusNode])

  const showHandles = selected && editingEnabled

  return (
    <>
      {showHandles && CORNERS.map(corner => (
        <NodeResizeControl
          key={corner}
          position={corner}
          minWidth={MIN_AREA_SIZE}
          minHeight={MIN_AREA_SIZE}
          className="area-node__handle"
          onResizeStart={() => onResizeStart?.(note.id, corner, 'free')}
        />
      ))}
      {showHandles && EDGES.map(edge => (
        <NodeResizeControl
          key={edge}
          position={edge}
          variant={ResizeControlVariant.Line}
          minWidth={MIN_AREA_SIZE}
          minHeight={MIN_AREA_SIZE}
          className="area-node__edge"
          // Eine Kante führt EINE Achse; die andere bleibt, wo sie war —
          // auch beim Einrasten (`getFreeResizeAlignment`, `axes`).
          onResizeStart={() => onResizeStart?.(
            note.id,
            edge === 'top' || edge === 'left' ? 'top-left' : 'bottom-right',
            'free',
            { x: edge === 'left' || edge === 'right', y: edge === 'top' || edge === 'bottom' },
          )}
        />
      ))}

      <NodeToolbar
        isVisible={barPresence.mounted}
        position={placement === 'below' ? Position.Bottom : Position.Top}
        offset={OPTIONS_BAR_OFFSET}
      >
        <CanvasOptionsBar
          leaving={barPresence.leaving}
          onActionDone={returnFocus}
          // Farbe, Titel an/aus, Löschen — nicht mehr (Grillen 2026-09-04).
          buttons={['color', 'title', 'delete']}
          // Ohne eigene Farbe steht der Wähler auf Gelb — dem Ton, den der
          // Bereich dann trägt (siehe `getAreaColor`).
          colorKeys={[note.color ?? DEFAULT_CANVAS_NOTE_COLOR]}
          fontKeys={[]}
          titleOn={named}
          authorOn={false}
          placement={placement}
          onColorChange={key => edit({ color: note.color }, { color: key })}
          onColorPreview={setOwnPreview}
          // Kein Schrift-Knopf in `buttons`; der Rückruf ist nur der Typ-Pflicht geschuldet.
          onFontChange={() => undefined}
          onToggleTitle={handleToggleTitle}
          onDelete={() => (onDelete ? onDelete(note.id) : void dataSource.deleteCanvasNote(note.id))}
        />
      </NodeToolbar>

      <div
        ref={rootRef}
        className={`area-node${named ? ' area-node--named' : ''}${isEditing ? ' area-node--editing' : ''}`}
        style={{
          '--area-surface': color.surface,
          '--area-border': color.border,
          '--area-text': color.text,
          '--area-adopt-edge': getCanvasNoteRingColor(shownColor),
          '--area-tab-height': `${AREA_TAB_HEIGHT}px`,
          '--area-radius': `${UNION_CORNER_RADIUS}px`,
        } as React.CSSProperties}
        onPointerDown={event => {
          /*
            Innerhalb des **Auswahlrahmens** ist die Fläche der Griff, nicht
            der Bereich (CONTEXT.md): Wer eingesammelte Zettel am Rahmen
            zieht, greift zwischen ihnen sonst den Bereich — samt allem, was
            er trägt. Der Rahmen liegt im Portal und kann per `z-index` nie
            zwischen Bereich und Zettel kommen (der Node-Baum ist ein eigener
            Stapelkontext), also übergibt der Bereich. Das `preventDefault`
            dort hält React Flows eigenen Zug ab (kein `mousedown` mehr).
          */
          if (event.button !== 0 || !onFrameGrab) return
          const surface = document.querySelector('.canvas-selection__surface')
          if (!surface) return
          const box = surface.getBoundingClientRect()
          const inside = event.clientX >= box.left && event.clientX <= box.right
            && event.clientY >= box.top && event.clientY <= box.bottom
          if (inside) onFrameGrab(event)
        }}
        onDoubleClick={event => {
          // Doppelklick auf den Rand passt an den Inhalt an; auf der Fläche
          // nichts (Grillen 2026-09-04). Rand heißt: eine Handbreit innen.
          const box = rootRef.current?.getBoundingClientRect()
          if (!box || !onFitToContent) return
          const inset = 12
          const nearEdge = event.clientX - box.left < inset || box.right - event.clientX < inset
            || event.clientY - box.top < inset || box.bottom - event.clientY < inset
          if (nearEdge) onFitToContent(note.id)
        }}
      >
        <svg
          className="area-node__shape"
          width={width}
          height={height + (named ? AREA_TAB_HEIGHT : 0)}
          style={{ top: named ? -AREA_TAB_HEIGHT : 0 }}
          viewBox={`0 0 ${width} ${height + (named ? AREA_TAB_HEIGHT : 0)}`}
          aria-hidden="true"
        >
          <path className="area-node__fill" d={path} />
        </svg>
        {named && (
          <div
            className="area-node__tab"
            style={{ top: -AREA_TAB_HEIGHT, width: tabDomWidth }}
            // Doppelklick auf den Reiter benennt. Er liegt ÜBER der Karte —
            // für den Rand-Doppelklick oben wäre das „nah an der Oberkante",
            // deshalb bleibt das Ereignis hier.
            onDoubleClick={event => {
              event.stopPropagation()
              openEditor()
            }}
          >
            <CanvasAreaIcon size={24} className="area-node__tab-icon" />
            {/* Der Zwilling: misst die Breite des Namens (oder Platzhalters) in der Schrift des Reiters. */}
            <span ref={measureRef} className="area-node__measure" aria-hidden="true">{label}</span>
            {isEditing ? (
              <input
                ref={inputRef}
                className="area-node__input nodrag"
                style={{ width: textWidth }}
                value={draft ?? ''}
                placeholder={placeholder}
                autoFocus
                onChange={e => setDraft(e.target.value)}
                onKeyDown={handleInputKeyDown}
                onBlur={commit}
              />
            ) : draft ? (
              // Vollständig beim Überfahren: Der Reiter schneidet lange Namen ab.
              <WithTooltip content={draft}>
                <span className="area-node__title">{draft}</span>
              </WithTooltip>
            ) : (
              // Eingeschaltet, aber leer: derselbe Platzhalter wie im Feld,
              // damit der Wechsel nichts verrückt.
              <span className="area-node__title area-node__title--placeholder">{placeholder}</span>
            )}
          </div>
        )}
        <div className="area-node__selection" />
      </div>
    </>
  )
}
